/* =========================
  Cart Drawer (FULL FILE)
  - Fix: drawer opens from URL ?openCartDrawer=true
  - Fix: subscription change waits for line ids, never sends invalid updates
  - Fix: loader always turns off
  - Fix: GraphQL cart query includes sellingPlanAllocation
  ========================= */

  class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartToken = null;
    this.lineItems = [];
    this._subscriptionRetryCount = 0;
    this._maxSubscriptionRetries = 15;
    this._isNormalizingFrequency = false;

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", this.close.bind(this));

    this.bindUIElements();

    this.setHeaderCartIconAccessibility();
    this.setHeaderCartIconsAccessibility();
    toggleProgressBarVisibilityOnUpdate();

    this.initCartSession();

    // Returning from checkout (incl. bfcache) often reopens a frozen drawer with
    // leftover subscription prices — re-normalize to one-off totals.
    window.addEventListener("pageshow", () => {
      if (this.classList.contains("active") && !this.classList.contains("is-empty")) {
        this.prepareCartTotalsOnOpen();
      }
    });
  }

  async initCartSession() {
    // Lightweight boot: only resolve cart token. Defer GraphQL line-ids + one-off
    // clears until open (when needed) or frequency apply / checkout.
    this._cartSessionPromise = (async () => {
      try {
        await this.getCartToken();
      } catch (err) {
        console.warn('initCartSession getCartToken', err);
      }
    })();

    return this._cartSessionPromise;
  }

  bindUIElements() {
    this.purchaseOptions = this.querySelector("#selling-plan-options");

    // IMPORTANT: keep the same handler reference so removeEventListener works
    if (!this._onSubscriptionChangedBound) {
      this._onSubscriptionChangedBound = this.onSubscriptionChanged.bind(this);
    }

    if (this.purchaseOptions) {
      this.purchaseOptions.removeEventListener("change", this._onSubscriptionChangedBound);
      this.purchaseOptions.addEventListener("change", this._onSubscriptionChangedBound);
    }
  }

  toggleCartDrawerLoader(forceState) {
    const cartDrawerLoader = this.querySelector(".cart-drawer-loader");
    if (!cartDrawerLoader) return;

    // forceState: true = show, false = hide, undefined = toggle
    if (typeof forceState === "boolean") {
      cartDrawerLoader.classList.toggle("hidden", !forceState);
      return;
    }

    cartDrawerLoader.classList.toggle("hidden");
  }

syncDeliveryFrequencyUI() {
  const mealItems = Array.from(this.querySelectorAll('.cart-item.--item-is-meal'));

  if (!mealItems.length) return;

  // Delivery frequency UI is global (one set of radios)
  const fieldset = this.querySelector('#selling-plan-options');
  if (!fieldset) return;

  const radios = Array.from(
    fieldset.querySelectorAll('input[type="radio"][name="purchase_option"]')
  );

  // Clear all radios first
  radios.forEach(radio => {
    radio.checked = false;
  });

  // Use first meal item as source of truth
  const item = mealItems[0];
  const lineId = item.getAttribute('data-line-id');
  const line = this.lineItems.find(l => l.id === lineId);
  if (!line) return;

  const planContainer = item.querySelector('.selling-plan-options');
  if (!planContainer) return;

  const oneWeekId = planContainer.getAttribute('data-selling-plan-1-week-id');
  const twoWeekId = planContainer.getAttribute('data-selling-plan-2-weeks-id');

  // ONE-OFF
  if (!line.sellingPlanId) {
    const oneOffRadio = fieldset.querySelector('input[value=""]');
    if (oneOffRadio) oneOffRadio.checked = true;
    return;
  }

  // WEEKLY
  if (line.sellingPlanId === oneWeekId) {
    const weeklyRadio = fieldset.querySelector(
      'input[value="Delivery every 1 Week"]'
    );
    if (weeklyRadio) weeklyRadio.checked = true;
  }

  // FORTNIGHTLY
  if (line.sellingPlanId === twoWeekId) {
    const fortnightlyRadio = fieldset.querySelector(
      'input[value="Delivery every 2 Weeks"]'
    );
    if (fortnightlyRadio) fortnightlyRadio.checked = true;
  }
}


async onSubscriptionChanged(e) {
  const selectedValue = e?.target?.value;
  // Allow One-off (empty value), block only undefined/null
  if (selectedValue === undefined || selectedValue === null) {
    return;
  }

  // Wait until cart line IDs exist before mapping them to selling plans
  const itemNodes = Array.from(this.querySelectorAll('.cart-item'));
  const hasMissingLineIds = itemNodes.some((item) => !item.getAttribute('data-line-id'));

  if (!this.lineItems?.length || hasMissingLineIds) {
    setTimeout(() => this.onSubscriptionChanged(e), 150);
    return;
  }

  this.toggleCartDrawerLoader(true);

  try {
    const success = await this.applySelectedFrequencyToCart(selectedValue);
    if (success) {
      await this.refreshCart();
    }
  } catch (err) {
    console.error('Subscription update error', err);
  } finally {
    this.toggleCartDrawerLoader(false);
  }
}

// Apply a delivery frequency to every supporting cart line.
// Fast path: one Storefront cartLinesUpdate for all lines (checkout uses same cart token).
// Slow fallback: sequential Ajax only if GraphQL cannot run.
async applySelectedFrequencyToCart(selectedValue) {
  if (selectedValue === undefined || selectedValue === null) {
    const checkedRadio = this.querySelector(
      '#selling-plan-options input[name="purchase_option"]:checked'
    );
    selectedValue = checkedRadio ? checkedRadio.value : null;
  }

  if (selectedValue === undefined || selectedValue === null) {
    return false;
  }

  // Avoid racing a one-off wipe still in progress on cart open.
  if (this._ensuringOneOff) {
    try {
      await this._ensuringOneOff;
    } catch (err) {
      console.warn('applySelectedFrequencyToCart wait ensureOneOff', err);
    }
  }

  try {
    if (!this.cartToken) {
      await this.getCartToken();
    }
    if (!this.lineItems?.length) {
      await this.getCartLineIds({ skipNormalize: true });
    }

    // Prefer single GraphQL batch (~1 request for whole cart).
    if (this.cartToken && window.storeFrontAPIClient) {
      const graphOk = await this.applySelectedFrequencyViaGraphQL(selectedValue);
      if (graphOk) {
        return true;
      }
    }

    // Slow fallback: Ajax one line at a time
    let cart = await this.fetchAjaxCart();
    if (!cart?.items?.length) {
      return true;
    }
    await this.applyFrequencyViaAjax(selectedValue, cart);
    return true;
  } catch (err) {
    console.error('applySelectedFrequencyToCart error', err);
    return false;
  }
}

/**
 * GraphQL (Storefront cart) frequency update — used as a mirror of Ajax apply.
 */
async applySelectedFrequencyViaGraphQL(selectedValue) {
  if (!this.cartToken) return false;

  const linesForUpdate = Array.from(this.querySelectorAll('.cart-item'))
    .map((itemNode) => {
      const lineId = itemNode.getAttribute('data-line-id');
      if (!lineId) return null;

      const planContainer = itemNode.querySelector('.selling-plan-options');
      if (!planContainer) return null;

      let sellingPlanId = null;
      if (selectedValue === 'Delivery every 1 Week') {
        sellingPlanId = planContainer.getAttribute('data-selling-plan-1-week-id');
      } else if (selectedValue === 'Delivery every 2 Weeks') {
        sellingPlanId = planContainer.getAttribute('data-selling-plan-2-weeks-id');
      } else if (selectedValue === '') {
        sellingPlanId = null;
      }

      if (selectedValue !== '' && !sellingPlanId) {
        return null;
      }

      const line = (this.lineItems || []).find((l) => l.id === lineId);
      const currentPlanId = this.normalizeId(line?.sellingPlanId);
      const desiredPlanId = this.normalizeId(sellingPlanId);
      if (currentPlanId === desiredPlanId) {
        return null;
      }

      return { lineId, sellingPlanId };
    })
    .filter(Boolean);

  if (!linesForUpdate.length) {
    return true;
  }

  const cartMutation = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { id }
        userErrors { field message }
      }
    }
  `;

  const response = await window.storeFrontAPIClient.request(cartMutation, {
    variables: {
      cartId: `gid://shopify/Cart/${this.cartToken}`,
      lines: linesForUpdate.map((line) => ({
        id: line.lineId,
        sellingPlanId: line.sellingPlanId
          ? `gid://shopify/SellingPlan/${line.sellingPlanId}`
          : null,
      })),
    },
  });

  const errors = response?.data?.cartLinesUpdate?.userErrors || [];
  if (errors.length) {
    console.warn('applySelectedFrequencyViaGraphQL userErrors', errors);
    return false;
  }

  if (!response?.data?.cartLinesUpdate?.cart?.id) {
    return false;
  }

  linesForUpdate.forEach((updated) => {
    const line = (this.lineItems || []).find((l) => l.id === updated.lineId);
    if (line) {
      line.sellingPlanId = updated.sellingPlanId
        ? `gid://shopify/SellingPlan/${this.normalizeId(updated.sellingPlanId)}`
        : null;
    }
  });

  return true;
}

  /**
   * Snapshot current selling-plan IDs for every cart line (from lineItems).
   * Used by the delivery-frequency modal to restore state after price preview.
   */
  snapshotSellingPlans() {
    return (this.lineItems || []).map((line) => ({
      lineId: line.id,
      sellingPlanId: this.normalizeId(line.sellingPlanId) || null,
    }));
  }

  /**
   * Restore selling plans from a snapshot produced by snapshotSellingPlans().
   */
  async restoreSellingPlans(snapshot = []) {
    if (!this.cartToken || !Array.isArray(snapshot) || !snapshot.length) {
      return false;
    }

    const linesForUpdate = snapshot
      .map((entry) => {
        const line = (this.lineItems || []).find((l) => l.id === entry.lineId);
        if (!line) return null;

        const currentPlanId = this.normalizeId(line.sellingPlanId);
        const desiredPlanId = this.normalizeId(entry.sellingPlanId);
        if (currentPlanId === desiredPlanId) return null;

        return {
          lineId: entry.lineId,
          sellingPlanId: desiredPlanId || null,
        };
      })
      .filter(Boolean);

    if (!linesForUpdate.length) return true;

    const cartMutation = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { id }
          userErrors { field message }
        }
      }
    `;

    try {
      const response = await window.storeFrontAPIClient.request(cartMutation, {
        variables: {
          cartId: `gid://shopify/Cart/${this.cartToken}`,
          lines: linesForUpdate.map((line) => ({
            id: line.lineId,
            sellingPlanId: line.sellingPlanId
              ? `gid://shopify/SellingPlan/${line.sellingPlanId}`
              : null,
          })),
        },
      });

      const errors = response?.data?.cartLinesUpdate?.userErrors || [];
      if (errors.length) {
        console.warn('restoreSellingPlans userErrors', errors);
        return false;
      }

      if (!response?.data?.cartLinesUpdate?.cart?.id) return false;

      linesForUpdate.forEach((updated) => {
        const line = (this.lineItems || []).find((l) => l.id === updated.lineId);
        if (line) {
          line.sellingPlanId = updated.sellingPlanId
            ? `gid://shopify/SellingPlan/${this.normalizeId(updated.sellingPlanId)}`
            : null;
        }
      });

      return true;
    } catch (err) {
      console.error('restoreSellingPlans error', err);
      return false;
    }
  }

  /**
   * Fetch estimated cart totals (and discount allocations) from the Storefront API.
   * Returns { totalCents, subtotalCents, currencyCode, discountCents } or null.
   */
  async getCartPricing() {
    if (!this.cartToken || !window.storeFrontAPIClient) return null;

    const cartQuery = `query getCartPricing($id: ID!) {
      cart(id: $id) {
        cost {
          totalAmount { amount currencyCode }
          subtotalAmount { amount currencyCode }
        }
        discountAllocations {
          discountedAmount { amount currencyCode }
        }
      }
    }`;

    try {
      const response = await window.storeFrontAPIClient.request(cartQuery, {
        variables: { id: `gid://shopify/Cart/${this.cartToken}` },
      });

      const cart = response?.data?.cart;
      if (!cart?.cost?.totalAmount) return null;

      const toCents = (amount) => Math.round(parseFloat(amount || '0') * 100);
      const discountCents = (cart.discountAllocations || []).reduce(
        (sum, alloc) => sum + toCents(alloc?.discountedAmount?.amount),
        0
      );

      return {
        totalCents: toCents(cart.cost.totalAmount.amount),
        subtotalCents: toCents(cart.cost.subtotalAmount?.amount),
        currencyCode: cart.cost.totalAmount.currencyCode,
        discountCents,
      };
    } catch (err) {
      console.error('getCartPricing error', err);
      return null;
    }
  }

  /**
   * Map a delivery-frequency radio value to the selling plan ID for a cart line
   * DOM node (uses data-selling-plan-*-id attrs from cart-drawer-item).
   */
  resolveSellingPlanIdForItem(itemNode, selectedValue) {
    const planContainer = itemNode?.querySelector('.selling-plan-options');
    if (!planContainer) return null;

    if (selectedValue === 'Delivery every 1 Week') {
      return this.normalizeId(planContainer.getAttribute('data-selling-plan-1-week-id')) || null;
    }
    if (selectedValue === 'Delivery every 2 Weeks') {
      return this.normalizeId(planContainer.getAttribute('data-selling-plan-2-weeks-id')) || null;
    }
    if (selectedValue === '') {
      return null;
    }
    return null;
  }

  findCartItemNodeByVariantId(variantId) {
    const id = String(variantId);
    return (
      this.querySelector(`.cart-item[data-line-variant-id="${id}"]`) ||
      this.querySelector(`.cart-item[data-variant-id="${id}"]`)
    );
  }

  /**
   * Prefer matching by line key (unique) when available.
   */
  findCartItemNodeByKey(lineKey) {
    if (!lineKey) return null;
    return this.querySelector(`.cart-item[data-line-key="${lineKey}"]`);
  }

  async fetchAjaxCart({ force = false } = {}) {
    const now = Date.now();
    if (
      !force &&
      this._ajaxCartCache &&
      this._ajaxCartCacheAt &&
      now - this._ajaxCartCacheAt < 2500
    ) {
      return this._ajaxCartCache;
    }

    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js failed: ${response.status}`);
    const cart = await response.json();
    this._ajaxCartCache = cart;
    this._ajaxCartCacheAt = Date.now();
    // Reuse token from cart.js — avoids a second round-trip in getCartToken.
    if (cart?.token) this.cartToken = cart.token;
    return cart;
  }

  invalidateAjaxCartCache() {
    this._ajaxCartCache = null;
    this._ajaxCartCacheAt = 0;
  }

  /**
   * Update a single line's selling plan via Ajax Cart API.
   * Uses the logged-in customer session so automatic discounts can evaluate.
   * Returns the updated cart object.
   */
  async changeAjaxLineSellingPlan(lineKey, quantity, sellingPlanId) {
    const body = {
      id: lineKey,
      quantity,
    };

    // Clear selling plan with empty string — null is ignored by some Shopify versions.
    if (sellingPlanId) {
      body.selling_plan = Number(sellingPlanId);
    } else {
      body.selling_plan = '';
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`cart/change.js failed: ${response.status} ${text}`);
    }

    const cart = await response.json();
    this._ajaxCartCache = cart;
    this._ajaxCartCacheAt = Date.now();
    return cart;
  }

  pricingFromAjaxCart(cart) {
    if (!cart || cart.total_price == null) return null;
    return {
      totalCents: cart.total_price,
      subtotalCents: cart.items_subtotal_price ?? cart.original_total_price,
      currencyCode: cart.currency,
      discountCents: cart.total_discount || 0,
      itemCount: cart.item_count,
      hasSubscriptionLines: (cart.items || []).some(
        (item) => !!item.selling_plan_allocation?.selling_plan?.id
      ),
    };
  }

  /**
   * Line total for drawer display: keep product/automatic discounts,
   * add back order-level / checkout coupon allocations.
   */
  lineDisplayTotalFromAjaxItem(item, cart) {
    let total =
      item.final_line_price ??
      (item.final_price != null ? item.final_price * (item.quantity || 1) : 0);

    const orderTitles = new Set(
      (cart?.cart_level_discount_applications || [])
        .map((d) => d?.title)
        .filter(Boolean)
    );

    for (const allocation of item.discount_allocations || []) {
      const app = allocation.discount_application || {};
      const isOrder =
        orderTitles.has(app.title) ||
        (app.type === "discount_code" && app.target_selection === "all");
      if (isOrder) {
        total += allocation.amount || 0;
      }
    }

    if (!total || total <= 0) {
      total =
        item.original_line_price ??
        (item.original_price != null ? item.original_price * (item.quantity || 1) : 0);
    }

    return total;
  }

  /**
   * Cart total for drawer display / min-order gate.
   * Product discounts kept; checkout/order coupons excluded.
   */
  oneOffCentsFromAjaxCart(cart) {
    if (!cart || cart.total_price == null) return null;

    const items = cart.items || [];
    const hasSubscription = items.some(
      (item) => !!item.selling_plan_allocation?.selling_plan?.id
    );

    if (hasSubscription) {
      return cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price;
    }

    if (!items.length) return cart.total_price;

    const summed = items.reduce(
      (sum, item) => sum + this.lineDisplayTotalFromAjaxItem(item, cart),
      0
    );

    // Never prefer a coupon-zeroed total when lines still have value.
    if (summed > 0) return summed;

    return (
      cart.original_total_price ??
      cart.items_subtotal_price ??
      cart.total_price
    );
  }

  /**
   * Remove checkout discount codes so automatic product discounts can re-apply.
   */
  async clearCheckoutDiscountCodes(cart) {
    const hasCodes = (cart?.discount_codes || []).some((d) => !!d?.code);
    if (!hasCodes) return { cart, cleared: false };

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discount: "" }),
      });
      if (!response.ok) return { cart, cleared: false };

      const updated = await response.json();
      this._ajaxCartCache = updated;
      this._ajaxCartCacheAt = Date.now();
      return { cart: updated, cleared: true };
    } catch (err) {
      console.warn("clearCheckoutDiscountCodes", err);
      return { cart, cleared: false };
    }
  }

  /**
   * Update footer + modal attributes to one-off cents before plans finish clearing.
   */
  patchDisplayedOneOffTotal(cents) {
    if (cents == null || !Number.isFinite(Number(cents))) return;

    const amount = Number(cents);
    const checkoutHost = this.querySelector("checkout-button");
    if (checkoutHost) {
      checkoutHost.setAttribute("data-cart-cost", String(amount));
    }

    const totalEl = this.querySelector(".cart-journey__total-value");
    if (totalEl) {
      let formatted = "";
      if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
        formatted = Shopify.formatMoney(amount);
      } else {
        formatted = `$${(amount / 100).toFixed(2)}`;
      }
      totalEl.textContent = String(formatted)
        .replace(/^[A-Z]{1,3}\s?(?=\$)/i, "")
        .trim();
    }

    this.patchCartCheckoutSummary(amount);

    const modal = document.querySelector("delivery-frequency-modal");
    if (modal) {
      modal.setAttribute("data-cart-total", String(amount));
      // Drop stale cached modal pricing built from the discounted total.
      if (modal._pricingCache) {
        modal._pricingCache = null;
      }
    }
  }

  /**
   * Live-update the 3-phase checkout summary when totals change without a full re-render.
   */
  patchCartCheckoutSummary(cents) {
    const summary = this.querySelector("[data-cart-summary]");
    if (!summary || cents == null) return;

    const minCents = parseInt(summary.getAttribute("data-min-cents"), 10) || 6500;
    const fsCents = parseInt(summary.getAttribute("data-fs-cents"), 10) || 15000;
    const amount = Number(cents);

    let phase = 1;
    if (amount >= fsCents) phase = 3;
    else if (amount >= minCents) phase = 2;

    summary.setAttribute("data-phase", String(phase));
    summary.setAttribute("data-cart-total", String(amount));
    summary.classList.remove(
      "cart-checkout-summary--phase-1",
      "cart-checkout-summary--phase-2",
      "cart-checkout-summary--phase-3"
    );
    summary.classList.add(`cart-checkout-summary--phase-${phase}`);

    const format = (value) => {
      let formatted = "";
      if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
        formatted = Shopify.formatMoney(value);
      } else {
        formatted = `$${(value / 100).toFixed(2)}`;
      }
      return String(formatted).replace(/^[A-Z]{1,3}\s?(?=\$)/i, "").trim();
    };

    const title = summary.querySelector(".cart-journey__headline");
    const subtitle = summary.querySelector(".cart-journey__sub");
    const fill = summary.querySelector(".cart-journey__fill");
    const minMilestone = summary.querySelector(".cart-journey__node--min");
    const shipMilestone = summary.querySelector(".cart-journey__node--ship");
    const minLabel = summary.querySelector(".cart-journey__label--min");
    const shipLabel = summary.querySelector(".cart-journey__label--ship");
    const helper = this.querySelector(".cart-journey__hint");

    const toMin = Math.max(minCents - amount, 0);
    const toShip = Math.max(fsCents - amount, 0);
    const progress = Math.max(0, Math.min(100, (amount / fsCents) * 100));
    const minReached = amount >= minCents;
    const shipReached = amount >= fsCents;
    const minMarkerPct = Math.max(0, Math.min(100, (minCents / fsCents) * 100));

    if (title && subtitle) {
      if (phase === 1) {
        title.textContent = `Add ${format(toMin)} to checkout`;
        subtitle.textContent = `Checkout unlocks at ${format(minCents)}. Free shipping unlocks at ${format(fsCents)}.`;
      } else if (phase === 2) {
        title.textContent = "You're ready to checkout";
        subtitle.textContent = `${format(minCents)} minimum reached · Add ${format(toShip)} more for free shipping.`;
      } else {
        title.textContent = "Free shipping unlocked";
        subtitle.textContent = "Your minimum order is reached and your order ships free.";
      }
    }

    if (fill) fill.style.width = `${progress}%`;
    summary.querySelector(".cart-journey__track")?.setAttribute("aria-valuenow", String(Math.round(progress)));
    if (minMilestone) {
      minMilestone.style.left = `${minMarkerPct}%`;
      minMilestone.classList.toggle("is-reached", minReached);
    }
    if (shipMilestone) {
      shipMilestone.style.left = "100%";
      shipMilestone.classList.toggle("is-reached", shipReached);
    }
    if (minLabel) {
      minLabel.style.setProperty("--pct", String(minMarkerPct));
      minLabel.classList.toggle("is-reached", minReached);
      minLabel.textContent = minReached
        ? `${format(minCents).replace(/\.00$/, "")} minimum reached`
        : `${format(minCents).replace(/\.00$/, "")} minimum`;
    }
    if (shipLabel) {
      shipLabel.classList.toggle("is-reached", shipReached);
      shipLabel.textContent = shipReached
        ? "Free shipping unlocked"
        : `Free shipping ${format(fsCents).replace(/\.00$/, "")}`;
    }

    if (helper) {
      const checkoutHost = this.querySelector("checkout-button");
      if (toMin > 0 && checkoutHost?.hasAttribute("data-min-cost") && checkoutHost.dataset.cartDiscountBypass !== "true") {
        helper.hidden = false;
        helper.textContent = `Add ${format(toMin)} to continue to checkout.`;
      } else {
        helper.hidden = true;
      }
    }
  }

  /**
   * Clear any leftover selling plans so the live cart (and drawer footer)
   * show the full one-off total. Pricing previews must never leave plans on.
   * Fast path: one GraphQL batch instead of N × cart/change.js.
   */
  async ensureOneOffCartState() {
    if (this._ensuringOneOff) {
      return this._ensuringOneOff;
    }

    this._ensuringOneOff = (async () => {
      let cart;
      try {
        cart = await this.fetchAjaxCart();
      } catch (err) {
        console.warn('ensureOneOffCartState fetch', err);
        return { oneOffCents: null, cleared: false };
      }

      const hasSubscription = (cart.items || []).some(
        (item) => !!item.selling_plan_allocation?.selling_plan?.id
      );
      const oneOffCents = this.oneOffCentsFromAjaxCart(cart);

      // Immediately show full one-off total (don't leave discounted SSR total visible).
      if (oneOffCents != null) {
        this.patchDisplayedOneOffTotal(oneOffCents);
      }

      if (!hasSubscription) {
        return { oneOffCents, cleared: false };
      }

      this._isPreviewingPricing = true;
      try {
        // Fast path: clear every plan in one Storefront mutation.
        const clearedGraph = await this.clearAllSellingPlansViaGraphQL();
        if (clearedGraph) {
          // Don't block UI on full section re-render — footer already patched.
          this.refreshCart().catch((err) =>
            console.warn('ensureOneOffCartState refreshCart', err)
          );
          return { oneOffCents, cleared: true };
        }

        // Slow fallback: sequential Ajax clears.
        let safety = 0;
        while (safety++ < 30) {
          const pending = (cart.items || []).find(
            (item) => !!item.selling_plan_allocation?.selling_plan?.id
          );
          if (!pending) break;
          cart = await this.changeAjaxLineSellingPlan(
            pending.key,
            pending.quantity,
            null
          );
        }

        this.refreshCart().catch((err) =>
          console.warn('ensureOneOffCartState refreshCart', err)
        );
        const refreshed = this.oneOffCentsFromAjaxCart(cart) ?? oneOffCents;
        return { oneOffCents: refreshed, cleared: true };
      } catch (err) {
        console.error('ensureOneOffCartState clear error', err);
        return { oneOffCents, cleared: false };
      } finally {
        this._isPreviewingPricing = false;
      }
    })();

    try {
      return await this._ensuringOneOff;
    } finally {
      this._ensuringOneOff = null;
    }
  }

  /**
   * Clear all line selling plans in a single GraphQL cartLinesUpdate.
   */
  async clearAllSellingPlansViaGraphQL() {
    if (!window.storeFrontAPIClient) return false;

    if (!this.cartToken) {
      await this.getCartToken();
    }
    if (!this.cartToken) return false;

    if (!this.lineItems?.length) {
      await this.getCartLineIds({ skipNormalize: true });
    }

    const linesForUpdate = (this.lineItems || [])
      .filter((line) => !!this.normalizeId(line.sellingPlanId))
      .map((line) => ({
        id: line.id,
        sellingPlanId: null,
      }));

    // Also try DOM data-line-id when GraphQL list incomplete.
    if (!linesForUpdate.length) {
      Array.from(this.querySelectorAll('.cart-item[data-line-id]')).forEach((node) => {
        const lineId = node.getAttribute('data-line-id');
        if (lineId) {
          linesForUpdate.push({ id: lineId, sellingPlanId: null });
        }
      });
    }

    if (!linesForUpdate.length) return false;

    const cartMutation = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { id }
          userErrors { field message }
        }
      }
    `;

    try {
      const response = await window.storeFrontAPIClient.request(cartMutation, {
        variables: {
          cartId: `gid://shopify/Cart/${this.cartToken}`,
          lines: linesForUpdate,
        },
      });

      const errors = response?.data?.cartLinesUpdate?.userErrors || [];
      if (errors.length) {
        console.warn('clearAllSellingPlansViaGraphQL userErrors', errors);
        return false;
      }

      if (!response?.data?.cartLinesUpdate?.cart?.id) return false;

      (this.lineItems || []).forEach((line) => {
        line.sellingPlanId = null;
      });

      return true;
    } catch (err) {
      console.error('clearAllSellingPlansViaGraphQL error', err);
      return false;
    }
  }

  /**
   * @deprecated Prefer estimate-only modal pricing. Kept for rare callers;
   * must fully restore plans and refresh so the drawer never stays discounted.
   */
  async previewPricingForFrequency(selectedValue, options = {}) {
    if (selectedValue === undefined || selectedValue === null) {
      return null;
    }

    this._isPreviewingPricing = true;

    let cart;
    try {
      cart = await this.fetchAjaxCart();
    } catch (err) {
      console.error('previewPricingForFrequency fetch error', err);
      this._isPreviewingPricing = false;
      return null;
    }

    const oneOffCents = this.oneOffCentsFromAjaxCart(cart);
    const snapshot = (cart.items || []).map((item) => ({
      variantId: String(item.variant_id),
      quantity: item.quantity,
      sellingPlanId: this.normalizeId(item.selling_plan_allocation?.selling_plan?.id) || null,
      key: item.key,
    }));

    try {
      cart = await this.applyFrequencyViaAjax(selectedValue, cart);
      const pricing = this.pricingFromAjaxCart(cart);

      if (!pricing) return null;

      return {
        ...pricing,
        oneOffCents,
      };
    } catch (err) {
      console.error('previewPricingForFrequency apply error', err);
      return null;
    } finally {
      try {
        // Always force one-off after preview — don't rely on snapshot restore alone.
        await this.applyFrequencyViaAjax('', await this.fetchAjaxCart());
      } catch (err) {
        console.error('previewPricingForFrequency clear error', err);
        try {
          await this.restoreAjaxSellingPlans(snapshot);
        } catch (restoreErr) {
          console.error('previewPricingForFrequency restore error', restoreErr);
        }
      }

      try {
        await this.refreshCart();
      } catch (err) {
        console.warn('previewPricingForFrequency refresh error', err);
      }

      this._isPreviewingPricing = false;
    }
  }

  /**
   * Walk Ajax cart lines and set each to the desired selling plan for selectedValue.
   * Re-reads keys after each change because Shopify regenerates line keys.
   */
  async applyFrequencyViaAjax(selectedValue, cart) {
    let currentCart = cart;
    let safety = 0;

    while (safety++ < 40) {
      const items = currentCart.items || [];
      const pending = items.find((item) => {
        const itemNode =
          this.findCartItemNodeByKey(item.key) ||
          this.findCartItemNodeByVariantId(item.variant_id);
        const desired =
          this.normalizeId(this.resolveSellingPlanIdForItem(itemNode, selectedValue)) || null;

        // Cannot apply a subscription without a plan mapping — skip this line.
        if (selectedValue !== '' && !desired) return false;

        const current =
          this.normalizeId(item.selling_plan_allocation?.selling_plan?.id) || null;
        return current !== (desired || null);
      });

      if (!pending) break;

      const itemNode =
        this.findCartItemNodeByKey(pending.key) ||
        this.findCartItemNodeByVariantId(pending.variant_id);
      const desired =
        this.normalizeId(this.resolveSellingPlanIdForItem(itemNode, selectedValue)) || null;

      currentCart = await this.changeAjaxLineSellingPlan(
        pending.key,
        pending.quantity,
        desired
      );
    }

    return currentCart;
  }

  /**
   * Restore selling plans from an Ajax cart snapshot (variant + prior plan).
   */
  async restoreAjaxSellingPlans(snapshot = []) {
    if (!Array.isArray(snapshot) || !snapshot.length) return;

    let cart = await this.fetchAjaxCart();
    let safety = 0;

    while (safety++ < 30) {
      const pending = (cart.items || []).find((item) => {
        const match = snapshot.find(
          (entry) =>
            entry.variantId === String(item.variant_id) &&
            entry.quantity === item.quantity
        );
        if (!match) {
          // Fall back: any snapshot row with same variant.
          const byVariant = snapshot.find(
            (entry) => entry.variantId === String(item.variant_id)
          );
          if (!byVariant) return false;
          const current =
            this.normalizeId(item.selling_plan_allocation?.selling_plan?.id) || null;
          return current !== byVariant.sellingPlanId;
        }
        const current =
          this.normalizeId(item.selling_plan_allocation?.selling_plan?.id) || null;
        return current !== match.sellingPlanId;
      });

      if (!pending) break;

      const match =
        snapshot.find(
          (entry) =>
            entry.variantId === String(pending.variant_id) &&
            entry.quantity === pending.quantity
        ) ||
        snapshot.find((entry) => entry.variantId === String(pending.variant_id));

      cart = await this.changeAjaxLineSellingPlan(
        pending.key,
        pending.quantity,
        match?.sellingPlanId || null
      );
    }
  }

  /**
   * Read totals from the Online Store Ajax cart after selling plans have been
   * applied. This carries the logged-in customer context for automatic discounts.
   */
  async getAjaxCartPricing() {
    try {
      const cart = await this.fetchAjaxCart();
      return this.pricingFromAjaxCart(cart);
    } catch (err) {
      console.error('getAjaxCartPricing error', err);
      return null;
    }
  }


  async getCartToken() {
    if (this.cartToken) return this.cartToken;

    // Reuse cached cart.js when available.
    try {
      const cart = await this.fetchAjaxCart();
      if (cart?.token) {
        this.cartToken = cart.token;
        return this.cartToken;
      }
    } catch (e) {
      console.error("cdebug error", e);
    }

    console.log("cdebug cart token", this.cartToken);
    return this.cartToken;
  }

  async getCartLineIds({ skipNormalize = false } = {}) {
    let response;

    const cartQuery = `query getCartGQL($id: ID!) {
      cart(id: $id) {
        id
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  product { title }
                  title
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
      }
    }`;

    try {
      response = await window.storeFrontAPIClient.request(cartQuery, {
        variables: { id: `gid://shopify/Cart/${this.cartToken}` },
      });
    } catch (e) {
      console.error("cdebug error", e);
      return;
    }

    console.log("cdebug cart gql", response);

    if (Array.isArray(response?.data?.cart?.lines?.edges)) {
      this.formatAndSaveLineIds(response.data.cart.lines.edges);
      this.printLineIdsToDOM();
      // Skip auto-normalize during frequency apply / one-off clear (saves a mutation + refresh).
      if (!skipNormalize) {
        await this.normalizeMealFrequenciesToWeeklyIfMixed();
      }
    }
  }
  syncDeliveryFrequencyUI() {
  const mealItems = Array.from(this.querySelectorAll('.cart-item.--item-is-meal'));

  mealItems.forEach(item => {
    const lineId = item.getAttribute('data-line-id');
    const line = this.lineItems.find(l => l.id === lineId);
    if (!line) return;

    const planContainer = item.querySelector('.selling-plan-options');
    if (!planContainer) return;

    const oneWeekId = planContainer.getAttribute('data-selling-plan-1-week-id');
    const twoWeekId = planContainer.getAttribute('data-selling-plan-2-weeks-id');

    const radios = item.querySelectorAll('[data-delivery-option]');
    radios.forEach(radio => radio.classList.remove('is-active'));

    if (!line.sellingPlanId) {
      item.querySelector('[data-delivery-option="one-off"]')?.classList.add('is-active');
      return;
    }

    if (line.sellingPlanId === oneWeekId) {
      item.querySelector('[data-delivery-option="1-week"]')?.classList.add('is-active');
    }

    if (line.sellingPlanId === twoWeekId) {
      item.querySelector('[data-delivery-option="2-weeks"]')?.classList.add('is-active');
    }
  });
}


  async refreshCart() {
    let response;
    let responseStatus;

    try {
      response = await fetch(window.Shopify.routes.root + `?sections=cart-drawer`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }).then((res) => {
        responseStatus = res.status;
        return res.json();
      });
    } catch (e) {
      console.error("cdebug error", e);
      return;
    }

    if (responseStatus === 200) {
      [
        {
          id: "cart-drawer",
          selector: "#CartDrawer",
        },
      ].forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        sectionElement.innerHTML = this.getSectionInnerHTML(response[section.id], section.selector);
      });
    }

    await this.getCartLineIds({ skipNormalize: true });
    this.bindUIElements();
    this.syncDeliveryFrequencyUI();

    console.log(
      "cdebug cart response",
      new DOMParser().parseFromString(response["cart-drawer"], "text/html").querySelector(".cart-items")
    );
  }

  printLineIdsToDOM() {
    const lineItemsDOM = Array.from(this.querySelectorAll(".cart-item"));

    lineItemsDOM.forEach((lineItemDOM) => {
      const lineItemDOMVariant = lineItemDOM.getAttribute("data-line-variant-id");
      const lineItem = this.lineItems.find((li) => li.variantId == lineItemDOMVariant);

      if (lineItem) {
        lineItemDOM.setAttribute("data-line-id", lineItem.id);
      }
    });
  }

  formatAndSaveLineIds(lineItems) {
    this.lineItems = lineItems.map((item) => {
      const lineItem = item.node;
      const variantId = lineItem?.merchandise?.id?.replace("gid://shopify/ProductVariant/", "");

      return {
        id: lineItem.id,
        variantId,
        sellingPlanId: lineItem.sellingPlanAllocation?.sellingPlan?.id || null,
        sellingPlanName: lineItem.sellingPlanAllocation?.sellingPlan?.name || null,
      };
    });

    hideBoosterItems();
    console.log("cdebug lineitems", this.lineItems);
  }

  normalizeId(value) {
    if (value === null || value === undefined || value === "") return "";
    return String(value).split("/").pop();
  }

  async normalizeMealFrequenciesToWeeklyIfMixed() {
    if (this._isNormalizingFrequency || this._isPreviewingPricing || !this.cartToken) return;

    const mealItems = Array.from(this.querySelectorAll(".cart-item.--item-is-meal"));
    if (!mealItems.length) return;

    const lines = mealItems
      .map((mealLineItem) => {
        const lineId = mealLineItem.getAttribute("data-line-id");
        const planContainer = mealLineItem.querySelector(".selling-plan-options");
        const weeklyPlanId = planContainer?.getAttribute("data-selling-plan-1-week-id");
        if (!lineId || !weeklyPlanId) return null;

        const line = this.lineItems.find((l) => l.id === lineId);
        if (!line) return null;

        const currentPlanId = this.normalizeId(line.sellingPlanId);
        return {
          lineId,
          weeklyPlanId: this.normalizeId(weeklyPlanId),
          currentPlanId,
        };
      })
      .filter(Boolean);

    if (!lines.length) return;

    const distinctPlanIds = new Set(lines.map((line) => line.currentPlanId));
    const hasWeeklyLine = lines.some((line) => line.currentPlanId === line.weeklyPlanId);
    const hasNonWeeklyLine = lines.some((line) => line.currentPlanId !== line.weeklyPlanId);

    // Normalize only when cart has mixed frequencies and weekly is present.
    if (distinctPlanIds.size <= 1 || !hasWeeklyLine || !hasNonWeeklyLine) return;

    this._isNormalizingFrequency = true;
    this.toggleCartDrawerLoader(true);

    try {
      const cartMutation = `
        mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart { id }
            userErrors { field message }
          }
        }
      `;

      const mutationVariables = {
        cartId: `gid://shopify/Cart/${this.cartToken}`,
        lines: lines.map((line) => ({
          id: line.lineId,
          sellingPlanId: `gid://shopify/SellingPlan/${line.weeklyPlanId}`,
        })),
      };

      const response = await window.storeFrontAPIClient.request(cartMutation, {
        variables: mutationVariables,
      });

      const errors = response?.data?.cartLinesUpdate?.userErrors || [];
      if (errors.length) {
        console.warn("normalizeMealFrequenciesToWeeklyIfMixed userErrors", errors);
        return;
      }

      if (response?.data?.cartLinesUpdate?.cart?.id) {
        await this.refreshCart();
      }
    } catch (err) {
      console.error("normalizeMealFrequenciesToWeeklyIfMixed error", err);
    } finally {
      this.toggleCartDrawerLoader(false);
      this._isNormalizingFrequency = false;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
      addCartNote && addCartNote();
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
        addCartNote && addCartNote();
      }
    });
  }

  setHeaderCartIconsAccessibility() {
    const cartLinks = Array.from(document.querySelectorAll(".cart-icon-bubble"));

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      cartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(cartLink);
        addCartNote && addCartNote();
      });

      cartLink.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(cartLink);
          addCartNote && addCartNote();
        }
      });
    });

    window.initCollectionProgess && window.initCollectionProgess();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");

        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");

    // Normalize totals only when needed — one-off carts open with no full-screen wait.
    if (!this.classList.contains("is-empty")) {
      this.prepareCartTotalsOnOpen();
    }
  }

  /**
   * Show drawer loader only when leftover subscription plans must be cleared.
   * Common one-off carts open instantly (no blocker network for UI).
   */
  async prepareCartTotalsOnOpen() {
    if (this.classList.contains("is-empty")) return;
    if (this._isPreparingOnOpen) {
      return this._isPreparingOnOpen;
    }

    this._isPreparingOnOpen = (async () => {
      try {
        // Instant path: no selling-plan labels in drawer HTML → trust SSR one-off totals.
        const hasPlanLabels = !!this.querySelector(
          ".cart-item .cart-item__selling-plan, .cart-item [class*='selling']"
        );
        // Most carts: no plan labels. Warm token in background and enable checkout immediately.
        // A light /cart.js check still runs without a full-screen loader.
        if (!hasPlanLabels) {
          this.restoreCheckoutEnabledState();

          try {
            let cart = await this.fetchAjaxCart();
            const { cart: cartAfterCodes, cleared } =
              await this.clearCheckoutDiscountCodes(cart);
            cart = cartAfterCodes;

            if (cleared) {
              this.toggleCartDrawerLoader(true);
              this.setCheckoutCalcGuard(true);
              await this.refreshCart();
              return;
            }

            const hasSubscription = (cart.items || []).some(
              (item) => !!item.selling_plan_allocation?.selling_plan?.id
            );
            const oneOffCents = this.oneOffCentsFromAjaxCart(cart);
            if (oneOffCents != null) this.patchDisplayedOneOffTotal(oneOffCents);
            if (!hasSubscription) return;

            this.toggleCartDrawerLoader(true);
            this.setCheckoutCalcGuard(true);
            await this.ensureOneOffCartState();
          } catch (err) {
            console.warn("prepareCartTotalsOnOpen light check", err);
          } finally {
            this.toggleCartDrawerLoader(false);
            this.setCheckoutCalcGuard(false);
            this.restoreCheckoutEnabledState();
          }
          return;
        }

        this.toggleCartDrawerLoader(true);
        this.setCheckoutCalcGuard(true);
        try {
          const cart = await this.fetchAjaxCart();
          const { cleared } = await this.clearCheckoutDiscountCodes(cart);
          if (cleared) await this.refreshCart();
        } catch (err) {
          console.warn("prepareCartTotalsOnOpen clear codes", err);
        }
        await this.ensureOneOffCartState();
      } catch (err) {
        console.warn("prepareCartTotalsOnOpen", err);
      } finally {
        this.toggleCartDrawerLoader(false);
        this.setCheckoutCalcGuard(false);
        this.restoreCheckoutEnabledState();
      }
    })();

    try {
      await this._isPreparingOnOpen;
    } finally {
      this._isPreparingOnOpen = null;
    }
  }

  setCheckoutCalcGuard(isGuarding) {
    this._isCalculatingTotals = !!isGuarding;
    const checkoutBtn = this.querySelector("#CartDrawer-Checkout");
    if (!checkoutBtn) return;

    if (isGuarding) {
      checkoutBtn.setAttribute("disabled", "");
      checkoutBtn.setAttribute("aria-busy", "true");
    } else {
      checkoutBtn.removeAttribute("aria-busy");
    }
  }

  /**
   * Re-apply original min-cost / bypass rules after calculation guard ends.
   */
  restoreCheckoutEnabledState() {
    const host = this.querySelector("checkout-button");
    const checkoutBtn = this.querySelector("#CartDrawer-Checkout");
    if (!checkoutBtn) return;

    if (this.classList.contains("is-empty")) {
      checkoutBtn.setAttribute("disabled", "");
      return;
    }

    if (!host) {
      checkoutBtn.removeAttribute("disabled");
      return;
    }

    const bypass = host.getAttribute("data-cart-discount-bypass") === "true";
    if (bypass) {
      checkoutBtn.removeAttribute("disabled");
      return;
    }

    const minCostAttr = host.getAttribute("data-min-cost");
    if (minCostAttr) {
      const minCost = parseInt(minCostAttr, 10) || 0;
      const cartCost = parseInt(host.getAttribute("data-cart-cost"), 10) || 0;
      if (cartCost < minCost) {
        checkoutBtn.setAttribute("disabled", "");
        return;
      }
    }

    checkoutBtn.removeAttribute("disabled");
  }

  close() {
    this.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling?.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, showToastInstead, source) {
    let quantity = parsedState.quantity || parsedState?.["item_count"];

    if (quantity == 0) {
      this?.classList.add("is-empty");
      this.querySelector(".drawer__inner")?.classList.add("is-empty");
    } else {
      this?.classList.remove("is-empty");
      this.querySelector(".drawer__inner")?.classList.remove("is-empty");
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const cartLinks = Array.from(document.querySelectorAll(".cart-icon-bubble"));
    cartLinks.forEach((cartLink) => {
      cartLink.innerHTML = document.querySelector("#cart-icon-bubble").innerHTML;
    });

    window.initCollectionProgess && window.initCollectionProgess();

    const collectionProgressBar = document.querySelector(".collection-progress-bar");

    if (quantity > 0) {
      collectionProgressBar && collectionProgressBar.classList.add("--active");
      this.getCartToken();
      this.getCartLineIds();
    } else {
      collectionProgressBar && collectionProgressBar.classList.remove("--active");
    }

    toggleProgressBarVisibilityOnUpdate();

    if (source != "cre-quantity-sync") {
      if (showToastInstead && typeof Toastify !== "undefined") {
        const toastMessage = window?.variantStrings?.addToCartToast?.replace("[product-name]", parsedState.title);

        const toastConfig = {
          text: toastMessage,
          duration: 5000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "left",
          stopOnFocus: true,
          style: { background: "rgba(var(--color-button),var(--alpha-button-background))" },
          onClick: function () {},
        };

        if (window.prevToast && window.prevToast.hideToast) window.prevToast.hideToast();
        window.prevToast = Toastify(toastConfig);
        window.prevToast.showToast();
      } else {
        document.querySelector("cart-drawer")?.open();
      }
    }

    this.bindUIElements();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML || "";
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

/* ===== Open cart drawer from URL ?openCartDrawer=true ===== */
function openCartDrawerFromURL() {
  const params = new URLSearchParams(window.location.search);
  const shouldOpen = params.get("openCartDrawer");

  if (!shouldOpen) return;

  const attemptOpen = () => {
    const drawer = document.querySelector("cart-drawer");
    if (drawer && typeof drawer.open === "function") {
      drawer.open();
      return true;
    }
    return false;
  };

  if (attemptOpen()) return;

  let attempts = 0;
  const interval = setInterval(() => {
    attempts += 1;
    if (attemptOpen() || attempts > 20) clearInterval(interval);
  }, 100);
}

document.addEventListener("DOMContentLoaded", () => {
  openCartDrawerFromURL();
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

class ClearCart extends HTMLElement {
  constructor() {
    super();
    this.clearButton = this.querySelector("button");
    if (this.clearButton) {
      this.clearButton.addEventListener("click", this.onClickClearCart.bind(this));
    }
  }

  onClickClearCart(event) {
    event.preventDefault();

    const url = "/cart/clear.js";
    const data = {
      sections: document
        .querySelector("cart-drawer-items")
        .getSectionsToRender()
        .map((section) => section.section),
      sections_url: window.location.pathname,
    };

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        document.querySelector("cart-drawer-items").getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          elementToReplace.innerHTML = document
            .querySelector("cart-drawer-items")
            .getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        const collectionProgressBar = document.querySelector(".collection-progress-bar");
        if (collectionProgressBar) collectionProgressBar.classList.remove("--active");
        toggleProgressBarVisibilityOnUpdate();

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "clear-cart",
          productVariantId: null,
          sectionUpdates: [],
          incomingQty: 0,
        });

        if (document.querySelector("cre-price-sync")?.syncPriceToProduct) {
          document.querySelector("cre-price-sync").syncPriceToProduct();
        }

        document.querySelector("cart-drawer")?.classList.add("is-empty");

        const quantityInputs = document.querySelectorAll("quantity-input:not(cart-drawer .quantity-input)");
        const visibleQuantityInputs = Array.from(quantityInputs).filter((input) => !input.classList.contains("hidden"));
        if (visibleQuantityInputs.length === 0) return;

        visibleQuantityInputs.forEach((each) => {
          const removedProductVariantId = each.dataset.vid;
          const productremove = new CustomEvent(`remove-product--${removedProductVariantId}`, {
            detail: { quantity: 0 },
          });
          document.dispatchEvent(productremove);
        });

        const cartLinks = Array.from(document.querySelectorAll(".cart-icon-bubble"));
        cartLinks.forEach((cartLink) => {
          cartLink.innerHTML = document.querySelector("#cart-icon-bubble").innerHTML;
        });

        window.initCollectionProgess && window.initCollectionProgess();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }
}

customElements.define("clear-cart", ClearCart);

function toggleProgressBarVisibilityOnUpdate() {
  const mealItems = Array.from(document.querySelectorAll(".cart-item.--item-is-meal"));
  const cartDrawerFooter = document.querySelector(".cart-drawer__footer");
  const collectionBarWrapper = document.querySelector(".collection-bar-wrapper");

  if (mealItems.length > 0) {
    cartDrawerFooter && cartDrawerFooter.classList.add("--active");
    collectionBarWrapper && collectionBarWrapper.classList.add("--active");
  } else {
    cartDrawerFooter && cartDrawerFooter.classList.remove("--active");
    collectionBarWrapper && collectionBarWrapper.classList.remove("--active");
  }
}
